import { WarningIcon } from '@plato/ui-legacy/assets/icons/warning-icon';
import { BaseConfirmationDialog } from '@plato/ui-legacy/components/dialog/confirmation-dialog';
import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { LinkText } from '@plato/ui-legacy/components/ui/link-text';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { TaskDeletedSource } from '../../analytics/types/analytics-events.ts';
import type { ModalInnerProps } from '../../modal/index.tsx';
import { useShowCreateTaskDialog } from '../hooks/use-show-create-task-dialog.ts';

export type DeleteTaskDialogProps = {
    taskId?: string;
    source: TaskDeletedSource;
    onDeleted?: () => void;
} & ModalInnerProps;

export const DeleteTaskDialog = DialogStateManager.create<DeleteTaskDialogProps>(
    ({ taskId, source, onDeleted }: DeleteTaskDialogProps) => {
        const { t } = useTranslation('tasks');
        const { toast } = useToast();
        const [isDeletingTask, setIsDeletingTask] = useState(false);
        const modal = useDialog();
        const apiUtils = api.useUtils();
        const sendEvent = useAnalytics();
        const showCreateTaskDialog = useShowCreateTaskDialog();

        const deleteMutation = api.platoUser.command.tasks.postTasksActions.delete.useMutation({
            onSuccess(deletedTask) {
                sendEvent('task_deleted', {
                    ...deletedTask,
                    source,
                    deletion_type: 'user_initiated',
                });

                void Promise.all([
                    apiUtils.platoUser.query.tasks.getManyTasks.getListWithFilters.invalidate(),
                    apiUtils.platoUser.query.tasks.getManyTasks.count.invalidate(),
                    apiUtils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate(),
                    apiUtils.platoUser.getManyCustomers.getListWithFilters.invalidate(),
                    apiUtils.platoUser.getManyQuotesOrders.getWithFilters.invalidate(),
                ]);

                toast({
                    icon: 'success',
                    title: t('tasks__toast__deleted_success_title'),
                    deletedLink: (
                        <LinkText
                            onClick={(e) => {
                                e.preventDefault();
                                showCreateTaskDialog({
                                    source: 'task_deleted_toast',
                                });
                                return null;
                            }}
                        />
                    ),
                });

                onDeleted?.();

                modal.remove();
            },
        });

        const handleSubmit = async () => {
            if (taskId) {
                setIsDeletingTask(true);

                deleteMutation.mutate({
                    taskId,
                });
            }
        };

        const handleCloseConfirmation = () => {
            modal.remove();
        };

        return (
            <BaseConfirmationDialog
                focus="destroy"
                title={t('tasks__delete_confirmation__title')}
                description={t('tasks__delete_confirmation__body')}
                icon={WarningIcon}
            >
                <BaseConfirmationDialog.Actions>
                    <BaseConfirmationDialog.RejectButton onClick={handleCloseConfirmation}>
                        {t('form__cancel', {
                            ns: 'common',
                        })}
                    </BaseConfirmationDialog.RejectButton>
                    <BaseConfirmationDialog.AcceptButton onClick={handleSubmit} isLoading={isDeletingTask}>
                        {t('form__delete', {
                            ns: 'common',
                        })}
                    </BaseConfirmationDialog.AcceptButton>
                </BaseConfirmationDialog.Actions>
            </BaseConfirmationDialog>
        );
    },
);
